import * as React from "react";
import { X, CheckCircle, AlertTriangle, Info, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

type AlertVariant = "success" | "warning" | "info" | "destructive";

interface InlineAlertProps {
  variant: AlertVariant;
  title?: string;
  children?: React.ReactNode;
  onDismiss?: () => void;
  className?: string;
}

const variantConfig: Record<AlertVariant, { icon: React.ComponentType<{ className?: string }>; styles: string }> = {
  success: {
    icon: CheckCircle,
    styles: "border-status-allow/30 bg-status-allow/10 text-foreground [&_svg]:text-status-allow",
  },
  warning: {
    icon: AlertTriangle,
    styles: "border-status-warning/30 bg-status-warning/10 text-foreground [&_svg]:text-status-warning",
  },
  info: {
    icon: Info,
    styles: "border-status-info/30 bg-status-info/10 text-foreground [&_svg]:text-status-info",
  },
  destructive: {
    icon: XCircle,
    styles: "border-destructive/30 bg-destructive/10 text-foreground [&_svg]:text-destructive",
  },
};

export function InlineAlert({ variant, title, children, onDismiss, className }: InlineAlertProps) {
  const { icon: Icon, styles } = variantConfig[variant];

  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-3 rounded-lg border px-4 py-3 text-sm",
        styles,
        className
      )}
    >
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        {title && <p className="font-medium leading-none mb-1">{title}</p>}
        {children && <div className="text-muted-foreground">{children}</div>}
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="shrink-0 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
